import React from 'react';
import {
  Button,
  Card,
  Col,
  Row,
  Space,
  Typography,
  Upload,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';

import type {
  HeroImageGroup,
  HeroImageUploadItem,
  HeroImagesJson,
} from '../PartnerCreatePage';

const { Text } = Typography;

const SUB_IMAGE_COUNT = 4;
const SLOT_SIZE = 112;

type DeviceType = 'mobile' | 'desktop';

interface MainImagesUploadSectionProps {
  value: HeroImagesJson;
  onChange: (nextValue: HeroImagesJson) => void;
}

interface ImageSlotProps {
  label: string;
  image: HeroImageUploadItem | null;
  onUpload: (file: File) => void;
  onRemove: () => void;
}

function createClientKey() {
  return `hero_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function createHeroImageItem(
  file: File,
  sortOrder: number,
  previous?: HeroImageUploadItem | null
): HeroImageUploadItem {
  if (previous?.previewUrl?.startsWith('blob:')) {
    URL.revokeObjectURL(previous.previewUrl);
  }

  return {
    clientKey: previous?.clientKey || createClientKey(),
    sourceType: 'UPLOAD',
    file,
    previewUrl: URL.createObjectURL(file),
    originalFileName: file.name,
    sortOrder,
  };
}

function normalizeGroup(group?: HeroImageGroup | null): HeroImageGroup {
  const items = Array.isArray(group?.items)
    ? group.items.slice(0, SUB_IMAGE_COUNT)
    : [];

  while (items.length < SUB_IMAGE_COUNT) {
    items.push(null);
  }

  return {
    main: group?.main || null,
    items,
  };
}

function normalizeHeroImagesJson(value?: HeroImagesJson | null): HeroImagesJson {
  return {
    mobile: normalizeGroup(value?.mobile),
    desktop: normalizeGroup(value?.desktop),
  };
}

function ImageSlot({
  label,
  image,
  onUpload,
  onRemove,
}: ImageSlotProps) {
  const previewUrl = image?.previewUrl;

  return (
    <div style={{ width: SLOT_SIZE }}>
      <Upload
        accept="image/*"
        showUploadList={false}
        maxCount={1}
        beforeUpload={(file) => {
          onUpload(file as unknown as File);
          return false;
        }}
      >
        <div
          style={{
            width: SLOT_SIZE,
            height: SLOT_SIZE,
            borderRadius: 10,
            border: '1px dashed #d9d9d9',
            background: '#fafafa',
            overflow: 'hidden',
            cursor: 'pointer',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={label}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : (
            <Space direction="vertical" align="center" size={4}>
              <PlusOutlined style={{ color: '#999', fontSize: 18 }} />
              <Text type="secondary" style={{ fontSize: 11 }}>
                업로드
              </Text>
            </Space>
          )}

          <div
            style={{
              position: 'absolute',
              left: 6,
              top: 6,
              padding: '2px 6px',
              borderRadius: 999,
              background: previewUrl ? 'rgba(0,0,0,0.55)' : '#fff',
              color: previewUrl ? '#fff' : '#666',
              fontSize: 11,
              lineHeight: '16px',
            }}
          >
            {label}
          </div>
        </div>
      </Upload>

      <Button
        danger
        size="small"
        block
        disabled={!previewUrl}
        icon={<DeleteOutlined />}
        onClick={onRemove}
        style={{ marginTop: 6 }}
      >
        삭제
      </Button>

      {image?.originalFileName && (
        <Text
          type="secondary"
          ellipsis
          style={{
            display: 'block',
            fontSize: 11,
            marginTop: 4,
            width: SLOT_SIZE,
          }}
        >
          {image.originalFileName}
        </Text>
      )}
    </div>
  );
}

export default function MainImagesUploadSection({
  value,
  onChange,
}: MainImagesUploadSectionProps) {
  const normalizedValue = normalizeHeroImagesJson(value);

  const updateMainImage = (device: DeviceType, file: File) => {
    const group = normalizeGroup(normalizedValue[device]);

    onChange({
      ...normalizedValue,
      [device]: {
        ...group,
        main: createHeroImageItem(file, 1, group.main),
      },
    });
  };

  const removeMainImage = (device: DeviceType) => {
    const group = normalizeGroup(normalizedValue[device]);

    if (group.main?.previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(group.main.previewUrl);
    }

    onChange({
      ...normalizedValue,
      [device]: {
        ...group,
        main: null,
      },
    });
  };

  const updateSubImage = (device: DeviceType, index: number, file: File) => {
    const group = normalizeGroup(normalizedValue[device]);
    const nextItems = [...group.items];

    nextItems[index] = createHeroImageItem(file, index + 2, nextItems[index]);

    onChange({
      ...normalizedValue,
      [device]: {
        ...group,
        items: nextItems,
      },
    });
  };

  const removeSubImage = (device: DeviceType, index: number) => {
    const group = normalizeGroup(normalizedValue[device]);
    const nextItems = [...group.items];

    if (nextItems[index]?.previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(nextItems[index]!.previewUrl!);
    }

    nextItems[index] = null;

    onChange({
      ...normalizedValue,
      [device]: {
        ...group,
        items: nextItems,
      },
    });
  };

  const renderDeviceSection = ({
    device,
    title,
    description,
  }: {
    device: DeviceType;
    title: string;
    description: string;
  }) => {
    const group = normalizeGroup(normalizedValue[device]);

    return (
      <Card size="small" title={title}>
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {description}
          </Text>

          <div
            style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <ImageSlot
              label="대표"
              image={group.main}
              onUpload={(file) => updateMainImage(device, file)}
              onRemove={() => removeMainImage(device)}
            />
            {
              device !== 'mobile' && (
                <div style={{display: 'flex', gap: 10,}}>
                  {group.items.map((item, index) => (
                    <ImageSlot
                      key={`${device}-sub-${index}`}
                      label={`추가 ${index + 1}`}
                      image={item}
                      onUpload={(file) => updateSubImage(device, index, file)}
                      onRemove={() => removeSubImage(device, index)}
                    />
                  ))}
                </div>
              )
            }
            
          </div>
        </Space>
      </Card>
    );
  };

  return (
    <Card size="small" title="상단 이미지">
      <Space direction="vertical" size={14} style={{ width: '100%' }}>
        <Text type="secondary">
          모바일과 데스크탑에서 사용할 대표 이미지 1장과 추가 이미지 4장을 업로드합니다.
        </Text>

        <Row gutter={[12, 12]}>
          <Col xs={24} xl={12}>
            {renderDeviceSection({
              device: 'mobile',
              title: '모바일',
              description: '모바일 화면에서 사용할 이미지입니다. 세로형 이미지가 적합합니다',
            })}
          </Col>

          <Col xs={24} xl={12}>
            {renderDeviceSection({
              device: 'desktop',
              title: '데스크탑',
              description: '데스크탑 화면에서 사용할 이미지입니다.',
            })}
          </Col>
        </Row>
      </Space>
    </Card>
  );
}